import {State} from '../../../analyticsStateMachines/THEOplayerAnalyticsStateMachine';
import {Event, EventMap} from '../../../enums/Event';
import type {NoExtraProperties} from '../../../types/NoExtraProperties';
import {logger} from '../../../utils/Logger';

import {CallbackScheduler} from './CallbackScheduler';

export interface DelayScenario {
  /** The event to delay dispatching */
  delayEvent: Event;
  /** Only delay when the state machine is in this state */
  inState: string;
  /** If any of these events arrive while the delayed event is pending, cancel it */
  cancelOnEvents: Event[];
}

export class DelayedEventDispatcher {
  private readonly scheduler: CallbackScheduler = new CallbackScheduler();
  private activeScenario: DelayScenario | undefined;

  static create(): DelayedEventDispatcher {
    return new DelayedEventDispatcher([
      // Delayed PAUSE event execution to handle the case: PRECEDING "PAUSE" EVENT ON STARTUP ERRORS
      {delayEvent: Event.PAUSE, inState: State.STARTUP, cancelOnEvents: [Event.ERROR]},

      // Delayed PAUSE event execution to handle the case: PRECEDING "PAUSE" EVENT BEFORE "SEEK" WHEN SEEKING VIA UI PROGRESS BAR
      {delayEvent: Event.PAUSE, inState: State.PLAYING, cancelOnEvents: [Event.SEEK]},

      // Delayed END_AD event execution to handle:
      // - MIDROLL & POSTROLL ADS DETERMINATION (cancelled by END)
      // - Player source unload during MIDROLL & POSTROLL ADS (cancelled by MANUAL_SOURCE_CHANGE)
      {
        delayEvent: Event.END_AD,
        inState: State.AD_PLAYBACK,
        cancelOnEvents: [Event.END, Event.MANUAL_SOURCE_CHANGE, Event.UNLOAD, Event.SOURCE_UNLOADED],
      },

      // Delayed END_AD event execution to handle: Player source change/unload during PREROLL ADS
      {
        delayEvent: Event.END_AD,
        inState: State.AD_PLAYBACK_IN_STARTUP,
        cancelOnEvents: [Event.MANUAL_SOURCE_CHANGE, Event.UNLOAD, Event.SOURCE_UNLOADED],
      },
    ]);
  }

  private constructor(private readonly scenarios: DelayScenario[]) {}

  dispatchEvent<StateMachineEvent extends keyof EventMap, StateMachineEventData extends EventMap[StateMachineEvent]>(
    event: StateMachineEvent,
    eventData: NoExtraProperties<EventMap[StateMachineEvent], StateMachineEventData>,
    state: string,
    dispatch: (
      event: StateMachineEvent,
      eventData: NoExtraProperties<EventMap[StateMachineEvent], StateMachineEventData>,
      eventTimestamp?: number,
    ) => void,
  ) {
    if (this.scheduler.isScheduled() && this.activeScenario != null) {
      if (this.activeScenario.cancelOnEvents.includes(event)) {
        logger.log(
          `⏳ [DelayedEventDispatcher] 🚫 Cancelling delayed '${String(this.activeScenario.delayEvent)}' — canceled by event '${String(event)}'`,
        );
        this.scheduler.cancel();
        this.activeScenario = undefined;
      } else {
        logger.log(
          `⏳ [DelayedEventDispatcher] ✅ Executing delayed '${String(this.activeScenario.delayEvent)}' early — unrelated event '${String(event)}' arrived`,
        );
        this.scheduler.execute();
        this.activeScenario = undefined;
      }
    }

    const matchingScenario = this.findMatchingScenario(event, state);
    if (matchingScenario != null) {
      const eventTimestamp = Date.now();
      this.activeScenario = matchingScenario;
      logger.log(`⏳ [DelayedEventDispatcher] ➕ Delaying '${String(event)}' in state '${state}'`);
      this.scheduler.schedule(() => {
        this.activeScenario = undefined;
        dispatch(event, eventData, eventTimestamp);
      });
      return;
    }

    dispatch(event, eventData);
  }

  /**
   * Immediately executes the pending delayed event, if there is one.
   *
   * Must be called before reading the state machine state outside of the player event flow
   * (e.g. {@link InternalAdapter.setCustomData}), so decisions are not made on a state
   * that the pending delayed event is about to change.
   */
  flushPendingEvent(): void {
    if (!this.scheduler.isScheduled()) {
      return;
    }
    logger.log(
      `⏳ [DelayedEventDispatcher] ✅ Executing delayed '${String(this.activeScenario?.delayEvent)}' early — flush requested`,
    );
    this.scheduler.execute();
    this.activeScenario = undefined;
  }

  private findMatchingScenario(event: keyof EventMap, state: string): DelayScenario | undefined {
    for (const scenario of this.scenarios) {
      if (scenario.delayEvent === event && scenario.inState === state) {
        return scenario;
      }
    }
    return undefined;
  }
}
